import React, { useCallback, useMemo, useState } from 'react'

import { useReactFlow } from '@xyflow/react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import Node, { InputType } from 'components_new/molecules/Node'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import { BOARD } from 'components_new/molecules/Node/accents'

import NodeType from 'types/transformation/enums/NodeType'
import {
  TextReplaceNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from '../hooks'

// The Text-replace accent (pink) input-focus tone, fixed like other accents.
const ACCENT = '#EC4899'

interface TextReplaceNodeProps {
  id: string
  data: TextReplaceNode
  selected: boolean
}

/** Full-width text input with a pink focus ring (find / replace / test). */
const TextInput = (props: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) => (
  <Box
    sx={{
      backgroundColor: BOARD.rowBackground,
      border: '1px solid transparent',
      borderRadius: '10px',
      '&:focus-within': { backgroundColor: 'common.white', borderColor: ACCENT }
    }}
  >
    <input
      className="nodrag ignore-locked-mode"
      onChange={(event) => props.onChange(event.target.value)}
      placeholder={props.placeholder}
      value={props.value}
      style={{
        background: 'none',
        border: 'none',
        boxSizing: 'border-box',
        color: BOARD.textPrimary,
        fontFamily: BOARD.fontBody,
        fontSize: '13px',
        outline: 'none',
        padding: '9px 11px',
        width: '100%'
      }}
    />
  </Box>
)

const TextReplace = (props: TextReplaceNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const [testInput, setTestInput] = useState('')

  // Preview mirrors the engine, which treats `search_text` as a regex.
  const testResult = useMemo(() => {
    if (!testInput || !data.search_text) {
      return testInput
    }

    try {
      return testInput.replace(
        new RegExp(data.search_text, 'g'),
        data.replace_text ?? ''
      )
    } catch {
      return 'Ogiltig regex'
    }
  }, [testInput, data.search_text, data.replace_text])

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<TextReplaceNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      left_column: pruneRef(nodeData.left_column, inputSchema)
    }),
    [inputSchema]
  )

  const updateField = useCallback(
    (partial: Partial<TextReplaceNode>) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: { ...(node.data as TextReplaceNode), ...partial }
              }
            : node
        )
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="TextReplace"
      inputType={InputType.SINGLE}
      title={'Text replace'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.TEXT_REPLACE}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '324px'
        }}
      >
        <FieldSelect
          value={data.left_column}
          options={fieldOptions}
          onChange={(columnId) => updateField({ left_column: columnId })}
          placeholder="Välj kolumn"
        />

        <Box>
          <Caption>Hitta</Caption>
          <TextInput
            value={data.search_text ?? ''}
            onChange={(value) => updateField({ search_text: value })}
            placeholder="Text att hitta…"
          />
        </Box>

        <Box>
          <Caption>Ersätt med</Caption>
          <TextInput
            value={data.replace_text ?? ''}
            onChange={(value) => updateField({ replace_text: value })}
            placeholder="Tom = ta bort texten"
          />
        </Box>

        <Box
          sx={{ borderTop: `1px solid ${BOARD.borderSoft}`, mt: 0.5, pt: 1 }}
        >
          <Caption>Testa</Caption>
          <TextInput
            value={testInput}
            onChange={setTestInput}
            placeholder="Klistra in en exempeltext…"
          />
        </Box>

        {testInput ? (
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: '#FDF2F8',
              border: '1px solid #FBCFE8',
              borderRadius: '10px',
              display: 'flex',
              gap: 1,
              p: '8px 11px'
            }}
          >
            <Icon
              name="ArrowForward"
              sx={{ color: '#DB2777', flex: 'none', fontSize: '14px' }}
            />
            <Box
              sx={{
                color: '#9D174D',
                fontFamily: BOARD.fontBody,
                fontSize: '13px',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {testResult}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Node>
  )
}

export default TextReplace
